import { useFilePicker } from '@/components/FilePicker/FilePickerProvider';
import { Container } from '@/components/Layout/Container';
import { Logo } from '@/components/Logo/Logo';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetBody, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { PROFILE_ROUTES, ROUTES } from '@/constants/constants';
import { Context } from '@/context/appContext';
import { useIsMobile } from '@/hooks/useIsMobile';
import { AppStateActions } from '@/reducers/appReducer';
import { LogOut, Menu, UserRound } from 'lucide-react';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useNavigate } from 'react-router-dom';

/** §7 site header: h65, 64px gutter, 1px bottom rule, white. */
const Header = () => {
  const { t, i18n } = useTranslation('web', { keyPrefix: 'components.layout.header' });
  const {
    state: { authToken },
    dispatch,
  } = useContext(Context);
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { open: openFilePicker } = useFilePicker();
  const [menuOpen, setMenuOpen] = useState(false);

  const lang = i18n.language;

  const navLinks = useMemo(
    () => [
      { to: ROUTES.home(lang), label: t('editPdf'), end: true },
      { to: ROUTES.convertToWord(lang), label: t('pdfToWord'), end: false },
    ],
    [lang, t],
  );

  // Clicking the logo returns to a blank slate; SET_DOCUMENT(null) triggers a full RESET_STATE.
  const handleLogoClick = useCallback(
    () => dispatch({ type: AppStateActions.SET_DOCUMENT, payload: { document: null } }),
    [dispatch],
  );

  const handleSignInClick = useCallback(
    () => dispatch({ type: AppStateActions.SET_AUTH_STATE, payload: { state: 'SIGN_IN' } }),
    [dispatch],
  );

  const handleSignOutClick = useCallback(() => {
    dispatch({ type: AppStateActions.SET_AUTH_TOKEN, payload: undefined });
    navigate(ROUTES.home(lang));
  }, [dispatch, lang, navigate]);

  const handleProfileClick = useCallback(
    (subRoute: PROFILE_ROUTES) => () => navigate(ROUTES.profile(lang, subRoute)),
    [lang, navigate],
  );

  const handleOpenEditor = useCallback(() => {
    setMenuOpen(false);
    openFilePicker();
  }, [openFilePicker]);

  const profileMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={t('userMenu.general')}>
          <UserRound size={18} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={handleProfileClick(PROFILE_ROUTES.general)}>
          {t('userMenu.general')}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={handleProfileClick(PROFILE_ROUTES.cards)}>{t('userMenu.cards')}</DropdownMenuItem>
        <DropdownMenuItem onSelect={handleProfileClick(PROFILE_ROUTES.subscriptions)}>
          {t('userMenu.subscriptions')}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={handleProfileClick(PROFILE_ROUTES.payments)}>
          {t('userMenu.payments')}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={handleSignOutClick}>
          <LogOut size={16} />
          {t('userMenu.logout')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <header className="shrink-0 border-b border-divider bg-background">
      <Container className="flex h-[65px] items-center gap-8">
        <NavLink to={ROUTES.home(lang)} onClick={handleLogoClick} aria-label={t('brand')}>
          <Logo />
        </NavLink>

        {isMobile ? (
          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetTrigger asChild className="ml-auto">
              <Button variant="ghost" size="icon" aria-label={t('allTools')}>
                <Menu size={18} />
              </Button>
            </SheetTrigger>
            <SheetContent side="right">
              <SheetHeader>
                <SheetTitle>{t('allTools')}</SheetTitle>
              </SheetHeader>
              <SheetBody className="flex flex-col gap-1">
                {navLinks.map((link) => (
                  <NavLink
                    key={link.to}
                    to={link.to}
                    end={link.end}
                    onClick={() => setMenuOpen(false)}
                    className="rounded-md px-3 py-2.5 text-[15px] text-ink-2 hover:bg-canvas hover:text-ink"
                  >
                    {link.label}
                  </NavLink>
                ))}

                <div className="my-3 h-px bg-divider" />

                {authToken ? (
                  <>
                    <NavLink
                      to={ROUTES.myFiles(lang)}
                      onClick={() => setMenuOpen(false)}
                      className="rounded-md px-3 py-2.5 text-[15px] text-ink-2 hover:bg-canvas hover:text-ink"
                    >
                      {t('myFiles')}
                    </NavLink>
                    <NavLink
                      to={ROUTES.profile(lang, PROFILE_ROUTES.general)}
                      onClick={() => setMenuOpen(false)}
                      className="rounded-md px-3 py-2.5 text-[15px] text-ink-2 hover:bg-canvas hover:text-ink"
                    >
                      {t('userMenu.general')}
                    </NavLink>
                    <Button
                      variant="ghost"
                      className="justify-start"
                      onClick={() => {
                        setMenuOpen(false);
                        handleSignOutClick();
                      }}
                    >
                      <LogOut size={16} />
                      {t('userMenu.logout')}
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="ghost"
                    className="justify-start"
                    onClick={() => {
                      setMenuOpen(false);
                      handleSignInClick();
                    }}
                  >
                    {t('logIn')}
                  </Button>
                )}

                <Button variant="ink" className="mt-3" onClick={handleOpenEditor}>
                  {t('openEditor')}
                </Button>
              </SheetBody>
            </SheetContent>
          </Sheet>
        ) : (
          <>
            <nav className="ml-3 flex items-center gap-[26px]">
              {navLinks.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.end}
                  className={({ isActive }) =>
                    `text-[15px] transition-colors duration-(--animate-duration-hover) ease-(--ease-standard) hover:text-ink ${
                      isActive ? 'text-ink' : 'text-ink-2'
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
            </nav>

            <div className="ml-auto flex items-center gap-4">
              {authToken ? (
                <>
                  <NavLink to={ROUTES.myFiles(lang)} className="text-[15px] text-ink-2 hover:text-ink">
                    {t('myFiles')}
                  </NavLink>
                  {profileMenu}
                </>
              ) : (
                <button
                  type="button"
                  onClick={handleSignInClick}
                  className="cursor-pointer rounded-md text-[15px] text-ink-2 hover:text-ink focus-visible:ring-focus"
                >
                  {t('logIn')}
                </button>
              )}
              <Button variant="ink" onClick={handleOpenEditor}>
                {t('openEditor')}
              </Button>
            </div>
          </>
        )}
      </Container>
    </header>
  );
};

export default Header;
